import { create } from 'zustand';

import { Format } from '@/modules/Format';
import { PointGuard } from '@/modules/PointGuard';
import { ReportCategoryGuard } from '@/modules/ReportCategoryGuard';
import type { GeocoderFeatureDto } from '@/modules/api';
import { backendApiGeocoderReverse } from '@/modules/api';
import type { CreateReportDtoFields, CreateReportState } from '@/types/CreateReportState';
import { CreateReportStep } from '@/types/CreateReportStep';
import { LocationInputMode } from '@/types/LocationInputMode';
import { isNonNullable } from '@/utils/isNonNullable';

export const transformCreateReportStateToDto = (state: CreateReportDtoFields) => {
  const { category, images, reportedByName, reportedByEmail, ...data } = state;

  return {
    ...data,
    ...(!!category && ReportCategoryGuard.isReportCategory(category) && { categoryId: category.id }),
    imageIds: images.map(({ reportImage }) => reportImage?.id).filter(isNonNullable),

    ...(reportedByEmail && { reportedByEmail }),
  };
};

const defaultCreateReportState: Omit<CreateReportState, 'actions'> = {
  expanded: false,
  finished: false,
  editLocation: false,

  step: CreateReportStep.LOCATION,

  locationInputMode: LocationInputMode.DEFAULT,

  geometry: undefined,
  address: undefined,
  geocoderFeatureId: undefined,
  category: undefined,
  routeSignLocationId: undefined,
  serviceStationId: undefined,
  description: undefined,
  images: [],

  termsOfUse: false,
  reportedByName: undefined,
  reportedByEmail: undefined,
};

export const useCreateReportStore = create<CreateReportState>()((set) => ({
  ...defaultCreateReportState,

  actions: {
    setExpanded: (expanded) => set(() => ({ expanded })),
    toggleExpanded: () => set(({ expanded }) => ({ expanded: !expanded })),
    toggleFinished: () => set(({ finished }) => ({ finished: !finished })),
    setFinished: (finished) => set(() => ({ finished })),
    setEditLocation: (editLocation) => set(() => ({ editLocation })),

    setStep: (step: CreateReportStep) => set({ step }),
    previousStep: () => set(({ step: currentStep }) => ({ step: Math.max(0, currentStep - 1) })),
    nextStep: () =>
      set(({ step: currentStep }) => ({ step: Math.min(Object.values(CreateReportStep).length, currentStep + 1) })),

    setLocationInputMode: (locationInputMode) => set({ locationInputMode }),

    updateData: (parameters) => {
      set(parameters);
    },
    updateGeometryWithReverseGeocoding: async (geometry) => {
      let feature: GeocoderFeatureDto | undefined;

      if (PointGuard.hasLongitudeAndLatitude(geometry)) {
        try {
          const { data } = await backendApiGeocoderReverse({
            path: {
              longitude: geometry.coordinates[0],
              latitude: geometry.coordinates[1],
            },
          });

          if (data) {
            feature = data;
          }
        } catch {
          //
        }
      }

      set({
        geometry,
        address: feature?.properties.text ?? Format.point(geometry),
        geocoderFeatureId: feature?.id,
      });
    },

    resetStore: () => set(defaultCreateReportState),
  },
}));
